import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import { usePagination } from '../hooks/usePagination';
import PaginationControls from '../components/PaginationControls';

const categoryPrefixMap = {
    "Traditional Jewels": "TTJ", "Kundans": "KND", "Kemps": "KMP", "Chokers": "CHK", "Chains": "CHN",
    "Rings": "RNG", "Bangles": "BGL", "Earrings": "ERG", "American Diamond": "AD", "Shoes": "SHS",
    "Bags": "BAG", "Interiors": "INT", "Statues": "STU", "Ornaments": "ORN", "Elixirs": "ELX",
    "Gifts": "GFT", "Victorian": "VIC", "Test1": "TS1", "Test2": "TS2", "Others": "OTR"
};
const allCategories = Object.keys(categoryPrefixMap);

const SalesHistory = ({ setView, preAppliedFilter }) => {
    const [allSales, setAllSales] = useState([]);
    const [filteredSales, setFilteredSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        founder: { label: 'Sold By', value: 'all', options: [] },
        category: { label: 'Category', value: 'all', options: allCategories }
    });

    const paginationProps = usePagination(filteredSales, 25);

    useEffect(() => {
        if (preAppliedFilter && preAppliedFilter.type === 'category') {
            setFilters(prev => ({ ...prev, category: { ...prev.category, value: preAppliedFilter.value } }));
        }
    }, [preAppliedFilter]);

    // ADDED: This is the data-fetching hook that was missing.
    useEffect(() => {
        setLoading(true);
        const salesQuery = query(collection(db, "sales"), orderBy("saleDate", "desc"));
        const unsubscribe = onSnapshot(salesQuery, (querySnapshot) => {
            const salesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), saleDate: doc.data().saleDate?.toDate() }));
            setAllSales(salesData);
            
            const founderNames = [...new Set(salesData.map(s => s.founderName))].filter(Boolean); // filter out null/undefined names
            setFilters(prev => ({ ...prev, founder: { ...prev.founder, options: founderNames } }));
            
            setLoading(false); // This correctly sets loading to false after data is fetched.
        }, (err) => {
            console.error("Error fetching sales data: ", err);
            setError("Could not load sales data.");
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // ADDED: This is the filtering hook that was missing.
    useEffect(() => {
        let results = [...allSales];
        
        if (filters.founder.value !== 'all') {
            results = results.filter(s => s.founderName === filters.founder.value);
        }
        
        if (filters.category.value !== 'all') {
            const prefix = categoryPrefixMap[filters.category.value];
            if (prefix) {
                results = results.filter(s => s.productIdString && s.productIdString.startsWith(prefix));
            }
        }

        if (searchTerm) {
            results = results.filter(s => 
                (s.productName && s.productName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (s.productIdString && s.productIdString.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        setFilteredSales(results);
    }, [searchTerm, filters, allSales]);

    const formatDate = (date) => date ? date.toLocaleDateString('en-GB') : 'N/A';

    if (loading) return <p>Loading sales history...</p>;
    if (error) return <p style={{ color: 'var(--brand-danger)' }}>{error}</p>;

    return (
        <div className="sales-history-container">
            <div className="page-header">
                <h2>Sales History</h2>
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    <SearchAndFilterBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} filters={filters} setFilters={setFilters} />
                    <button onClick={() => setView('companyDashboard')} className="close-button" style={{position: 'static'}}>&times;</button>
                </div>
            </div>
            <div className="table-wrapper">
                <table className="product-list-table">
                    <thead><tr><th>Sale Date</th><th>Product ID</th><th>Product Name</th><th>Qty</th><th>Sold By</th><th className="cell-right">Total Profit (RM)</th></tr></thead>
                    <tbody>
                        {paginationProps.paginatedData.length === 0 ? (
                            <tr><td colSpan="6" style={{textAlign: 'center'}}>No sales match your criteria.</td></tr>
                        ) : (
                            paginationProps.paginatedData.map(sale => (
                                <tr key={sale.id}>
                                    <td>{formatDate(sale.saleDate)}</td>
                                    <td>{sale.productIdString}</td>
                                    <td>{sale.productName}</td>
                                    <td>{sale.quantitySold}</td>
                                    <td>{sale.founderName}</td>
                                    <td className="cell-right" style={{color: 'var(--brand-success)', fontWeight: 'bold'}}>{Number(sale.totalProfit || 0).toFixed(2)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <PaginationControls paginationProps={paginationProps} />
        </div>
    );
};

export default SalesHistory;